import * as angular from 'angular';
import { Growl } from 'angular/custom/services/growl.service';
import { config } from 'angular/utils/config';
import { StorageOfferResource } from 'app/storage-offer/storage-offer.resource';
import { StorageOffersService } from 'app/storage-offer/storage-offers.service';
import { SsoSettingsResource } from 'app/_resources/sso-settings.resource';
import { TransmissionSystemOperatorResource } from 'app/_resources/transmission-system-operator.resource';
import { AuthService } from 'app/_services/auth.service';
import { ErrorHandler } from 'app/_services/error-handler.factory';

class LngOffersComponentCtrl implements ng.IController {
    public favoriteFilterText: string;
    public storageOffers: DataPageDto<StorageOfferDto>;
    public errorLoading: boolean = false;
    public errorDelete: boolean = false;
    public errorDeleteMessage: string;
    public loading: boolean = false;
    public itemsPerPage: number = 10;
    public currentPage: number = 1;
    public colFiltOpts: any = {};
    public extendFilterParamsForExport: any = {};
    public sortColumn: string;
    public sortAscending: boolean;
    public hasActiveFilter: boolean;
    public loggedInUser: LoginResult;
    public allocationEnabledForLoggedInSso: boolean;
    public params?: { operator?: string; offerLabel?: string };
    public operatorLabel: string;

    constructor(
        private lngOfferResource: StorageOfferResource,
        private COLUMN_FILTER: any,
        private columnFilterService: any,
        private authService: AuthService,
        private lngOffersService: StorageOffersService,
        private growl: Growl,
        private ssoSettingsResource: SsoSettingsResource,
        private errorHandler: ErrorHandler,
        private TransmissionSystemOperator: TransmissionSystemOperatorResource
    ) {
        const operator =
            this.params && this.params.operator
                ? this.params.operator
                : undefined;
        if (operator) {
            this.operatorLabel = Array.isArray(operator)
                ? operator.join(', ')
                : operator;
        } else {
            this.operatorLabel = '(All Operators)';
        }

        const offerLabel =
            this.params && this.params.offerLabel
                ? this.params.offerLabel
                : undefined;
        this.colFiltOpts.offerLabel = offerLabel ? [offerLabel] : [];
    }

    $onInit(): void {
        this.TransmissionSystemOperator.query(
            { detailLevel: 'MINIMAL' },
            (tsos: TsoMinimalDto[]) => {
                const operator =
                    this.params && this.params.operator
                        ? this.params.operator
                        : undefined;
                if (operator) {
                    this.colFiltOpts.tsoShortName = operator;
                } else if (config.lngOperators.length) {
                    this.colFiltOpts.tsoShortName = tsos
                        .filter((tso) =>
                            config.lngOperators.find(
                                (refId) => String(tso.id) === refId
                            )
                        )
                        .map((tso) => tso.shortName);
                } else {
                    this.colFiltOpts.tsoShortName = [
                        'NO_LSO_EXISTS_ON_THIS_STAGE'
                    ];
                }
                this.toggleQuickFilter(['FINISHED']);
            }
        );
        if (this.lngOffersService.offerDeletedSuccessfully) {
            this.showDeletionSuccessMessage();
            this.lngOffersService.offerDeletedSuccessfully = false;
        }

        if (this.loggedInUser && this.loggedInUser.role === 'SSO_ADMIN') {
            this.ssoSettingsResource.queryAllocationServiceSubscription(
                { id: this.loggedInUser.referenceId },
                (res: StoragePremiumServiceSubscriptionCreationDto) => {
                    this.allocationEnabledForLoggedInSso =
                        res.servicePackage != 'NO_ALGORITHM';
                },
                (err) => this.errorHandler(err)
            );
        }
    }

    private showDeletionSuccessMessage() {
        this.growl.show('Offer has been deleted.', 'success');
    }

    public toggleQuickFilter(
        filter: OfferState[],
        allocationFilter: AllocationState[] = []
    ) {
        this.colFiltOpts.offerState = filter;
        this.colFiltOpts.allocationState = allocationFilter;
        if (filter.length > 1) {
            this.sortColumn = 'offerState';
            this.sortAscending = true;
        } else {
            this.sortColumn = null;
            this.sortAscending = null;
        }
        this.loadStorageOffers();
    }

    public filterStorageOffers() {
        this.hasActiveFilter = this.columnFilterService.hasActiveFilter(
            this.colFiltOpts
        );
        const appliedFilters: string[] = [];
        if (
            this.colFiltOpts.offerLabel &&
            this.colFiltOpts.offerLabel.length > 0
        ) {
            appliedFilters.push('Offer label');
        }
        if (
            this.colFiltOpts.storageLocationName &&
            this.colFiltOpts.storageLocationName.length > 0
        ) {
            appliedFilters.push('Location');
        }
        if (
            this.colFiltOpts.tsoShortName &&
            this.colFiltOpts.tsoShortName.length > 0
        ) {
            appliedFilters.push('Operator');
        }
        if (
            this.colFiltOpts.productType &&
            this.colFiltOpts.productType.length > 0
        ) {
            appliedFilters.push('Product Type');
        }
        if (this.colFiltOpts.runtimeFrom || this.colFiltOpts.runtimeTo) {
            appliedFilters.push('Runtime');
        }
        if (
            this.colFiltOpts.offerState &&
            this.colFiltOpts.offerState.length > 0
        ) {
            appliedFilters.push('Status');
        }
        if (this.colFiltOpts.id && this.colFiltOpts.id.length > 0) {
            appliedFilters.push('ID');
        }
        if (this.hasActiveFilter) {
            appliedFilters.push('Active Offers');
        }
        if (!this.hasActiveFilter) {
            appliedFilters.push('All Offers');
        }
        this.loadStorageOffers();
    }

    public loadStorageOffers(page: number = 1) {
        this.loading = true;
        this.hasActiveFilter = this.columnFilterService.hasActiveFilter(
            this.colFiltOpts
        );

        let params = {
            start: (page - 1) * this.itemsPerPage,
            pageSize: this.itemsPerPage,
            sortColumn: this.sortColumn,
            sortAscending: this.sortAscending
        };

        angular.extend(params, this.colFiltOpts);
        this.lngOfferResource
            .page(
                params,
                (res) => {
                    this.storageOffers = res;
                },
                (err: any) => (this.errorLoading = true)
            )
            .$promise.finally(() => (this.loading = false));
    }

    public changeSortColumn(newSortColumn: string): void {
        this.sortColumn = newSortColumn;
        this.sortAscending = !this.sortAscending;
        this.currentPage = 1;
        this.loadStorageOffers();
    }

    public deleteOffer(offer: StorageOfferDto): void {
        this.lngOfferResource.delete(
            { id: offer.id },
            () => {
                this.storageOffers.data.splice(
                    this.storageOffers.data.indexOf(offer),
                    1
                );
                this.showDeletionSuccessMessage();
            },
            (err) => {
                this.errorDelete = true;
                this.errorDeleteMessage =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'An unknown error occurred. Please, check back later.';
            }
        );
    }

    public setExtendFilterParamsForExport(): void {
        this.extendFilterParamsForExport = {
            sortColumn: this.sortColumn,
            sortAscending: this.sortAscending
        };
        angular.extend(this.extendFilterParamsForExport, this.colFiltOpts);
    }
}

angular.module('app').component('pkpLngOffers', {
    controller: LngOffersComponentCtrl,
    template: require('app/lng-offer/lng-offers.component.html'),
    bindings: {
        params: '<?',
        favoriteFilterText: '<',
        loggedInUser: '<'
    }
});
